import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Link,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useCN54bisReport } from './hooks';
import { type CN54BisReportData, type BackendCN54BisData } from './types';
import CN54BisTable from './CN54BisTable';
import FilenameTable from './FilenameTable';
import CN56NonSampledTable from './CN56NonSampledTable';
import { transformBackendDataToSeparateTableData } from './dataTransformer';

import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
} from './CN54bisReport.styled';

interface DirectionOption {
  id: string;
  label: string;
}

interface CN54bisReportFilters {
  ppoValue: string;
  direction: string;
  year: string;
}

// Generate year options from 2008 to current year
const generateYearOptions = () => {
  const options: { value: string; label: string }[] = [];
  const currentYear = new Date().getFullYear();

  for (let year = currentYear; year >= 2008; year--) {
    options.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  return options;
};

function CN54bisReport() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const [filters, setFilters] = useState<CN54bisReportFilters>({
    ppoValue: '',
    direction: 'Inbound',
    year: '', // Default to 2019 as requested
  });

  // Results state
  const [results, setResults] = useState<CN54BisReportData | null>(null);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Get effective operators + cascading logic for e53 module (UPU TD Accounting)
  const {
    operators,
    availableYears,
    operatorsForYear,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e53', { selectedYear: filters.year, selectedPpo: filters.ppoValue });

  // CN54bis Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError,
  } = useCN54bisReport();

  // Generate year options with cascading logic
  const yearOptions = useMemo(() => {
    const yearsToShow = filters.ppoValue ? yearsForOperator : availableYears;
    return yearsToShow.map(year => ({ value: year.toString(), label: year.toString() }));
  }, [availableYears, yearsForOperator, filters.ppoValue]);

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/upu-td-accounting');
  };

  const handleDirectionChange = (id: string) => {
    setFilters((prev) => ({ ...prev, direction: id }));
  };

  // Handle DO selection change — years are independent of the selected DO
  const handlePpoChange = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, ppoValue: value }));
  }, []);

  // Handle year selection change with cascading behavior
  const handleYearChange = useCallback((value: string) => {
    setFilters((prev) => {
      // If DO is selected and not available for the new year, clear DO
      if (prev.ppoValue) {
        const relationsForNewYear = allRelationsData?.byYear?.[value];
        const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation =>
          relation.targetDO === prev.ppoValue &&
          relation.modules.some(module => module.name === 'e53' && module.enabled === true)
        );
        if (!isCurrentDOAvailableForNewYear) {
          return { ...prev, year: value, ppoValue: '' };
        }
      }
      return { ...prev, year: value };
    });
  }, [allRelationsData]);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleReset = () => {
    setFilters({
      ppoValue: '',
      direction: 'Inbound',
      year: '2019',
    });
    setResults(null);
    setNoDataFound(false);
    setSearchParams(new URLSearchParams());
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    if (!filters.year) {
      showSnackbar('Please select an Accounting Year');
      return;
    }

    // Update URL params with current filters when export is triggered
    applyFiltersToSectionBar();

    // Prepare API parameters
    const params = {
      ppoValue: filters.ppoValue || undefined,
      direction: filters.direction,
      year: filters.year,
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          console.log('Current filter params used:', params);
          
          // Check for 204 No Content or empty data
          if (!response.data) {
            // Set no data found state instead of clearing results
            setNoDataFound(true);
            setResults(null);
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          // Transform backend data to separate table format for each PPO
          const backendData = response.data as BackendCN54BisData[];
          const transformedTableData = transformBackendDataToSeparateTableData(backendData);
          
          if (transformedTableData.length === 0) {
            setNoDataFound(true);
            setResults(null);
            return;
          }

          // Create table data structure
          const newData: CN54BisReportData = {
            tables: transformedTableData,
            direction: filters.direction,
            backendData: backendData // Store original data for filename table
          };
          
          // Clear no data found state and set new data
          setNoDataFound(false);
          setResults(null);
          // Use setTimeout to ensure the state is cleared before setting new data
          setTimeout(() => {
            setResults(newData);
            console.log('Updated results state with transformed data:', newData);
          }, 0);
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(
            `${format} Report downloaded successfully with filename: ${response.filename}`
          );
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        // Handle NOT_FOUND errors for PDF/XLS exports
        if (format !== 'HTML') {
          let errorMessage = 'An error occurred while generating the report';
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No CN54bis reports available') || errorMessage.includes('No CN54')) {
            showSnackbar(
              errorMessage || 'No CN54bis reports available for the selected criteria',
              'warning'
            );
          } else {
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // For HTML format, show error snackbar
          showSnackbar(
            error instanceof Error ? error.message : 'Failed to generate report. Please try again.',
            'error'
          );
        }
      },
    });
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (filters.ppoValue) {
      newParams.append('ppo', filters.ppoValue);
    } else {
      newParams.append('ppo', 'All');
    }

    // Map direction
    newParams.append('direction', filters.direction);

    // Map year
    if (filters.year) {
      newParams.append('year', filters.year);
    }

    setSearchParams(newParams);
  };

  // Clear results callback
  const clearResults = useCallback(() => {
    setResults(null);
    setNoDataFound(false);
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Handler function for SectionBar PPO change
  const handleSectionBarPpoChange = useCallback((ppo: string) => {
    handlePpoChange(ppo);
  }, [handlePpoChange]);

  // Handler function for SectionBar year change
  const handleSectionBarYearChange = useCallback((year: string) => {
    handleYearChange(year);
  }, [handleYearChange]);

  // Handler function for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setFilters((prev) => ({ ...prev, direction }));
  }, []);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results
    if (!results || noDataFound) return undefined;

    return {
      ppo: filters.ppoValue || '',
      ppoOptions: (filters.year ? operatorsForYear : operators).map(targetDO => ({
        id: targetDO,
        name: targetDO,
        disabled: false,
      })),
      isPpoLoading: isPpoLoading,
      year: filters.year,
      yearOptions: (filters.ppoValue ? yearsForOperator : availableYears).map(year => year.toString()),
      direction: filters.direction,
      onPpoChange: handleSectionBarPpoChange,
      onYearChange: handleSectionBarYearChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    results,
    noDataFound,
    filters,
    operatorsForYear,
    operators,
    isPpoLoading,
    yearsForOperator,
    availableYears,
    handleSectionBarPpoChange,
    handleSectionBarDirectionChange,
    handleSectionBarYearChange,
    handleExport,
    isGeneratingReport,
  ]);

  // Initial URL parameter sync when page loads
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const directionParam = searchParams.get('direction');
    const yearParam = searchParams.get('year');

    if (ppoParam) {
      setFilters((prev) => ({ ...prev, ppoValue: ppoParam === 'All' ? '' : ppoParam }));
    }

    if (directionParam) {
      setFilters((prev) => ({ ...prev, direction: directionParam }));
    }

    if (yearParam) {
      setFilters((prev) => ({ ...prev, year: yearParam }));
    }
  }, [searchParams]);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='cn54bis-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='CN54bis Report'
            subtitle='UPU TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            module='e53'
            showFilters={{
              customCN54bis: !!results && !noDataFound,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="cn54bis-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='cn54bis-report-main-content'>
            <ReportContainer data-testid="cn54bis-report-content">
             

              {/* Header - Second Line */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid="cn54bis-report-header"
              >
                <Typography variant='h4' sx={{ fontWeight: 600, color: '#063142' }} data-testid="cn54bis-report-title">
                  CN54bis Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="cn54bis-report-header-actions">
                  {(results || noDataFound) && (
                    <ClearResultsButton variant='outlined' onClick={clearResults} data-testid="cn54bis-report-clear-results-btn">
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when results exist */}
              {!results && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="cn54bis-filter-section">
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="cn54bis-filter-controls">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn54bis-ppo-selector">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn54bis-ppo-label">
                          DO
                        </Typography>
                        <SelectContainer data-testid="cn54bis-ppo-select-container">
                          <FormControl size='small' fullWidth data-testid="cn54bis-ppo-form-control">
                            <Select
                              value={filters.ppoValue}
                              onChange={(e) => handlePpoChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              data-testid="cn54bis-ppo-select"
                            >
                              <MenuItem value='' data-testid="cn54bis-ppo-all-option">
                                {isPpoLoading ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="cn54bis-ppo-loading">
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                ) : (
                                  'All'
                                )}
                              </MenuItem>
                              {!isPpoLoading &&
                                !ppoError &&
                                (filters.year ? operatorsForYear : operators).map((targetDO) => (
                                  <MenuItem key={targetDO} value={targetDO} data-testid={`cn54bis-ppo-option-${targetDO}`}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                      }}
                                      data-testid={`cn54bis-ppo-option-content-${targetDO}`}
                                    >
                                      <span>{targetDO}</span>
                                     
                                    </Box>
                                  </MenuItem>
                                ))}
                              {ppoError && <MenuItem disabled data-testid="cn54bis-ppo-error">Error loading PPOs</MenuItem>}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl data-testid="cn54bis-direction-control">
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={filters.direction === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{
                              color: filters.direction === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px',
                            }}
                            data-testid={`cn54bis-direction-${option.id.toLowerCase()}`}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        mb: 2,
                      }}
                    />

                    {/* Accounting Year Selector */}
                    <Box sx={{ display: 'flex', gap: 2 }} data-testid="cn54bis-year-selector">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn54bis-year-control">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn54bis-year-label">
                          Accounting year: *
                        </Typography>
                        <SelectContainer data-testid="cn54bis-year-select-container">
                          <FormControl size='small' fullWidth data-testid="cn54bis-year-form-control">
                            <Select
                              value={filters.year}
                              onChange={(e) => handleYearChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                              data-testid="cn54bis-year-select"
                            >
                              <MenuItem value='' data-testid="cn54bis-year-placeholder">Select Year</MenuItem>
                              {(filters.ppoValue ? yearsForOperator : availableYears).map((year) => (
                                <MenuItem key={year} value={year.toString()} data-testid={`cn54bis-year-option-${year}`}>
                                  {year}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }} data-testid="cn54bis-action-buttons">
                    <ResetButton variant='contained' onClick={handleReset} data-testid="cn54bis-reset-button">
                      RESET
                    </ResetButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                      data-testid="cn54bis-export-html-button"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                      data-testid="cn54bis-export-pdf-button"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                      data-testid="cn54bis-export-xls-button"
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title="No Data Available"
                  message="No CN54bis report data was found for the selected criteria. Please try adjusting your filters or selecting a different year."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                  data-testid="cn54bis-no-data-found"
                />
              )}

              {/* Results Section - Show when results exist */}
              {results && !noDataFound && (
                <Box sx={{ mt: 6 }} data-testid="cn54bis-results-section">
                  <Box
                    sx={{
                      borderBottom: '2px solid #E1E8ED',
                      pb: 2,
                      mb: 4,
                    }}
                    data-testid="cn54bis-results-header"
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 700,
                        color: '#063142',
                        fontSize: '24px',
                        letterSpacing: '-0.5px',
                      }}
                      data-testid="cn54bis-results-title"
                    >
                      Report Results
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#666',
                        mt: 0.5,
                        fontSize: '14px',
                      }}
                      data-testid="cn54bis-results-description"
                    >
                      Generated CN54bis report data
                    </Typography>
                  </Box>
                  
                   {/* Display separate tables for each PPO/DO */}
                   {results.tables && results.tables.length > 0 ? (
                     results.tables.map((tableData, index) => {
                       return (
                         <Box key={tableData.ppo} sx={{ mb: 6 }} data-testid={`cn54bis-ppo-section-${tableData.ppo}`}>
                           {/* PPO Header */}
                           <Box
                             sx={{
                               mb: 3,
                               pb: 2,
                               borderBottom: '2px solid #E1E8ED',
                             }}
                             data-testid={`cn54bis-ppo-header-${tableData.ppo}`}
                           >
                             <Typography
                               variant='h5'
                               sx={{
                                 fontWeight: 700,
                                 color: '#063142',
                                 fontSize: '20px',
                                 letterSpacing: '-0.3px',
                               }}
                               data-testid={`cn54bis-ppo-title-${tableData.ppo}`}
                             >
                               Designated Operator: {tableData.ppo}
                             </Typography>
                           </Box>

                           {/* Display filename table for this PPO */}
                           <Box sx={{ mb: 4 }} data-testid={`cn54bis-filename-section-${tableData.ppo}`}>
                             <FilenameTable
                               e53FilenamesByQuarter={tableData.backendData.e53FilenamesByQuarter}
                               e55FilenamesByQuarter={tableData.backendData.e55FilenamesByQuarter}
                               ppo={tableData.ppo}
                               data-testid={`cn54bis-filename-table-${tableData.ppo}`}
                             />
                           </Box>
                           
                           {/* Format Separated Case Section */}
                           {tableData.formatSeparatedRows && tableData.formatSeparatedRows.length > 0 && (
                             <Box sx={{ mb: 4 }} data-testid={`cn54bis-format-separated-section-${tableData.ppo}`}>
                               <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }} data-testid={`cn54bis-format-separated-title-${tableData.ppo}`}>
                                 Format Separated Case
                               </Typography>
                               <Typography variant="body2" sx={{ mb: 2, color: '#666', fontSize: '14px' }} data-testid={`cn54bis-format-separated-description-${tableData.ppo}`}>
                                 Estimated quarterly number of items (data from CN54 and CN56), grand totals and overall IPKs
                               </Typography>
                               <Box
                                 sx={{
                                   '& .MuiPaper-root': {
                                     boxShadow: 'none',
                                     border: '1px solid #E1E8ED',
                                   },
                                 }}
                                 data-testid={`cn54bis-format-separated-table-container-${tableData.ppo}`}
                               >
                                 <CN54BisTable
                                   data={tableData.formatSeparatedRows}
                                   moduleName={``}
                                   viewMode={true}
                                   disableAutoSizing={false}
                                   pageSize={25}
                                   lazyLoad={tableData.formatSeparatedRows.length > 50}
                                   lazyLoadHeight={400}
                                   lazyLoadingBatchSize={25}
                                   rowBackgroundColor={results.direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
                                   data-testid={`cn54bis-format-separated-table-${tableData.ppo}`}
                                 />
                               </Box>
                             </Box>
                           )}

                           {/* Non-sampled flows for Format Separated Case */}
                           {tableData.formatSeparatedNonSampledRows && tableData.formatSeparatedNonSampledRows.length > 0 && (
                             <Box sx={{ mb: 4 }} data-testid={`cn54bis-format-separated-non-sampled-section-${tableData.ppo}`}>
                               <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }} data-testid={`cn54bis-format-separated-non-sampled-title-${tableData.ppo}`}>
                                 Non-sampled flows (data from CN56)
                               </Typography>
                               <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }} data-testid={`cn54bis-format-separated-non-sampled-tables-${tableData.ppo}`}>
                                 {/* Main CN56 Table */}
                                 <Box
                                   sx={{
                                     flex: 1,
                                     '& .MuiPaper-root': {
                                       boxShadow: 'none',
                                       border: '1px solid #E1E8ED',
                                     },
                                   }}
                                   data-testid={`cn56-format-separated-table-container-${tableData.ppo}`}
                                 >
                                   <CN56NonSampledTable
                                     data={tableData.formatSeparatedNonSampledRows}
                                     moduleName={``}
                                     viewMode={true}
                                     disableAutoSizing={false}
                                     pageSize={25}
                                     lazyLoad={false}
                                     lazyLoadHeight={400}
                                     lazyLoadingBatchSize={25}
                                     rowBackgroundColor={results.direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
                                     data-testid={`cn56-format-separated-non-sampled-table-${tableData.ppo}`}
                                   />
                                 </Box>

                                 {/* Small Grand Total Table on the right */}
                                 <Box sx={{ width: '250px', flexShrink: 0 }} data-testid={`cn54bis-format-separated-grand-total-container-${tableData.ppo}`}>
                                   <Box
                                     sx={{
                                       border: '1px solid #E1E8ED',
                                       borderRadius: '4px',
                                       overflow: 'hidden',
                                       backgroundColor: 'white',
                                     }}
                                     data-testid={`cn54bis-format-separated-grand-total-table-${tableData.ppo}`}
                                   >
                                     <Table size="small" sx={{ minWidth: 'auto' }} data-testid={`cn54bis-format-separated-grand-total-table-element-${tableData.ppo}`}>
                                       <TableHead data-testid={`cn54bis-format-separated-grand-total-head-${tableData.ppo}`}>
                                         {/* Parent header */}
                                         <TableRow sx={{ backgroundColor: '#E7EFF5' }} data-testid={`cn54bis-format-separated-grand-total-header-row-${tableData.ppo}`}>
                                           <TableCell 
                                             colSpan={2} 
                                             align="center"
                                             sx={{ 
                                               fontWeight: 700, 
                                               color: '#063142',
                                               fontSize: '14px',
                                               padding: '8px 12px',
                                               borderBottom: '1px solid #E1E8ED'
                                             }}
                                             data-testid={`cn54bis-format-separated-grand-total-title-${tableData.ppo}`}
                                           >
                                             Grand Total
                                           </TableCell>
                                         </TableRow>
                                         {/* Sub headers */}
                                         <TableRow sx={{ backgroundColor: '#F5F7FA' }} data-testid={`cn54bis-format-separated-grand-total-subheader-row-${tableData.ppo}`}>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontWeight: 600, 
                                               color: '#063142',
                                               fontSize: '12px',
                                               padding: '6px 8px'
                                             }}
                                             data-testid={`cn54bis-format-separated-grand-total-weight-header-${tableData.ppo}`}
                                           >
                                             Actual weight
                                           </TableCell>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontWeight: 600, 
                                               color: '#063142',
                                               fontSize: '12px',
                                               padding: '6px 8px'
                                             }}
                                             data-testid={`cn54bis-format-separated-grand-total-items-header-${tableData.ppo}`}
                                           >
                                             Number of items
                                           </TableCell>
                                         </TableRow>
                                       </TableHead>
                                       <TableBody data-testid={`cn54bis-format-separated-grand-total-body-${tableData.ppo}`}>
                                         <TableRow data-testid={`cn54bis-format-separated-grand-total-data-row-${tableData.ppo}`}>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontSize: '12px',
                                               padding: '6px 8px',
                                               fontWeight: 700
                                             }}
                                             data-testid={`cn54bis-format-separated-grand-total-weight-value-${tableData.ppo}`}
                                           >
                                             {tableData.backendData.formatSeparatedNonSampledFlow?.grandTotalRow?.actualWeightFormatted ?? 
                                              tableData.backendData.formatSeparatedNonSampledFlow?.grandTotalRow?.actualWeight ?? '-'}
                                           </TableCell>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontSize: '12px',
                                               padding: '6px 8px',
                                               fontWeight: 700
                                             }}
                                             data-testid={`cn54bis-format-separated-grand-total-items-value-${tableData.ppo}`}
                                           >
                                             {tableData.backendData.formatSeparatedNonSampledFlow?.grandTotalRow?.numberOfItemsFormatted ?? 
                                              tableData.backendData.formatSeparatedNonSampledFlow?.grandTotalRow?.numberOfItems ?? '-'}
                                           </TableCell>
                                         </TableRow>
                                       </TableBody>
                                     </Table>
                                   </Box>
                                 </Box>
                               </Box>
                             </Box>
                           )}

                           {/* Mixed Case Section */}
                           {tableData.mixedRows && tableData.mixedRows.length > 0 && (
                             <Box sx={{ mb: 4 }} data-testid={`cn54bis-mixed-section-${tableData.ppo}`}>
                               <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }} data-testid={`cn54bis-mixed-title-${tableData.ppo}`}>
                                 Mixed Case
                               </Typography>
                               <Typography variant="body2" sx={{ mb: 2, color: '#666', fontSize: '14px' }} data-testid={`cn54bis-mixed-description-${tableData.ppo}`}>
                                 Estimated quarterly number of items (data from CN54 and CN56), grand totals and overall IPKs
                               </Typography>
                               <Box
                                 sx={{
                                   '& .MuiPaper-root': {
                                     boxShadow: 'none',
                                     border: '1px solid #E1E8ED',
                                   },
                                 }}
                                 data-testid={`cn54bis-mixed-table-container-${tableData.ppo}`}
                               >
                                 <CN54BisTable
                                   data={tableData.mixedRows}
                                   moduleName={``}
                                   viewMode={true}
                                   disableAutoSizing={false}
                                   pageSize={25}
                                   lazyLoad={tableData.mixedRows.length > 50}
                                   lazyLoadHeight={400}
                                   lazyLoadingBatchSize={25}
                                   rowBackgroundColor={results.direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
                                   data-testid={`cn54bis-mixed-table-${tableData.ppo}`}
                                 />
                               </Box>
                             </Box>
                           )}

                           {/* Non-sampled flows for Mixed Case */}
                           {tableData.mixedNonSampledRows && tableData.mixedNonSampledRows.length > 0 && (
                             <Box sx={{ mb: 4 }} data-testid={`cn54bis-mixed-non-sampled-section-${tableData.ppo}`}>
                               <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }} data-testid={`cn54bis-mixed-non-sampled-title-${tableData.ppo}`}>
                                 Non-sampled flows (data from CN56)
                               </Typography>
                               <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }} data-testid={`cn54bis-mixed-non-sampled-tables-${tableData.ppo}`}>
                                 {/* Main CN56 Table */}
                                 <Box
                                   sx={{
                                     flex: 1,
                                     '& .MuiPaper-root': {
                                       boxShadow: 'none',
                                       border: '1px solid #E1E8ED',
                                     },
                                   }}
                                   data-testid={`cn56-mixed-table-container-${tableData.ppo}`}
                                 >
                                   <CN56NonSampledTable
                                     data={tableData.mixedNonSampledRows}
                                     moduleName={``}
                                     viewMode={true}
                                     disableAutoSizing={false}
                                     pageSize={25}
                                     lazyLoad={false}
                                     lazyLoadHeight={400}
                                     lazyLoadingBatchSize={25}
                                     rowBackgroundColor={results.direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
                                     data-testid={`cn56-mixed-non-sampled-table-${tableData.ppo}`}
                                   />
                                 </Box>

                                 {/* Small Grand Total Table on the right */}
                                 <Box sx={{ width: '250px', flexShrink: 0 }} data-testid={`cn54bis-mixed-grand-total-container-${tableData.ppo}`}>
                                   <Box
                                     sx={{
                                       border: '1px solid #E1E8ED',
                                       borderRadius: '4px',
                                       overflow: 'hidden',
                                       backgroundColor: 'white',
                                     }}
                                     data-testid={`cn54bis-mixed-grand-total-table-${tableData.ppo}`}
                                   >
                                     <Table size="small" sx={{ minWidth: 'auto' }} data-testid={`cn54bis-mixed-grand-total-table-element-${tableData.ppo}`}>
                                       <TableHead data-testid={`cn54bis-mixed-grand-total-head-${tableData.ppo}`}>
                                         {/* Parent header */}
                                         <TableRow sx={{ backgroundColor: '#E7EFF5' }} data-testid={`cn54bis-mixed-grand-total-header-row-${tableData.ppo}`}>
                                           <TableCell 
                                             colSpan={2} 
                                             align="center"
                                             sx={{ 
                                               fontWeight: 700, 
                                               color: '#063142',
                                               fontSize: '14px',
                                               padding: '8px 12px',
                                               borderBottom: '1px solid #E1E8ED'
                                             }}
                                             data-testid={`cn54bis-mixed-grand-total-title-${tableData.ppo}`}
                                           >
                                             Grand Total
                                           </TableCell>
                                         </TableRow>
                                         {/* Sub headers */}
                                         <TableRow sx={{ backgroundColor: '#F5F7FA' }} data-testid={`cn54bis-mixed-grand-total-subheader-row-${tableData.ppo}`}>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontWeight: 600, 
                                               color: '#063142',
                                               fontSize: '12px',
                                               padding: '6px 8px'
                                             }}
                                             data-testid={`cn54bis-mixed-grand-total-weight-header-${tableData.ppo}`}
                                           >
                                             Actual weight
                                           </TableCell>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontWeight: 600, 
                                               color: '#063142',
                                               fontSize: '12px',
                                               padding: '6px 8px'
                                             }}
                                             data-testid={`cn54bis-mixed-grand-total-items-header-${tableData.ppo}`}
                                           >
                                             Number of items
                                           </TableCell>
                                         </TableRow>
                                       </TableHead>
                                       <TableBody data-testid={`cn54bis-mixed-grand-total-body-${tableData.ppo}`}>
                                         <TableRow data-testid={`cn54bis-mixed-grand-total-data-row-${tableData.ppo}`}>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontSize: '12px',
                                               padding: '6px 8px',
                                               fontWeight: 700
                                             }}
                                             data-testid={`cn54bis-mixed-grand-total-weight-value-${tableData.ppo}`}
                                           >
                                             {tableData.backendData.mixedNonSampledFlow?.grandTotalRow?.actualWeightFormatted ?? 
                                              tableData.backendData.mixedNonSampledFlow?.grandTotalRow?.actualWeight ?? '-'}
                                           </TableCell>
                                           <TableCell 
                                             align="center"
                                             sx={{ 
                                               fontSize: '12px',
                                               padding: '6px 8px',
                                               fontWeight: 700
                                             }}
                                             data-testid={`cn54bis-mixed-grand-total-items-value-${tableData.ppo}`}
                                           >
                                             {tableData.backendData.mixedNonSampledFlow?.grandTotalRow?.numberOfItemsFormatted ?? 
                                              tableData.backendData.mixedNonSampledFlow?.grandTotalRow?.numberOfItems ?? '-'}
                                           </TableCell>
                                         </TableRow>
                                       </TableBody>
                                     </Table>
                                   </Box>
                                 </Box>
                               </Box>
                             </Box>
                           )}
                         </Box>
                       );
                     })
                   ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#FFF8E1',
                        borderRadius: '4px',
                        border: '1px dashed #FFE082',
                      }}
                    >
                      <Typography
                        sx={{
                          fontStyle: 'italic',
                          color: '#F57F17',
                          fontSize: '14px',
                          fontWeight: 500,
                        }}
                      >
                        ⚠️ No tables available for this report
                      </Typography>
                    </Box>
                  )}

                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='cn54bis-report-snackbar'
      />
    </Box>
  );
}

export default CN54bisReport;
